import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    product: '',
    quantity: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    try {
      // Backend handles both database save and WhatsApp notification
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you for contacting us! We will respond within 24 hours.');
      setFormData({ name: '', company: '', email: '', phone: '', product: '', quantity: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to submit form. Please try again or contact us via WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-light tracking-tight text-[#0B1B32] mb-6" data-testid="contact-title">
              Get in Touch
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              We're here to answer your questions and discuss how we can support your sourcing requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-medium text-[#0B1B32] mb-8">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F0F9FF] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#0B1B32]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#0B1B32] mb-1">Address</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      313, U.S Complex, Jasola Vihar,<br />
                      Delhi-110076, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F0F9FF] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#0B1B32]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#0B1B32] mb-1">Email</h3>
                    <a href="mailto:info@vistaglobaltrade.com" className="text-slate-600 text-sm hover:text-[#0B1B32] transition-colors">
                      info@vistaglobaltrade.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F0F9FF] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#0B1B32]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#0B1B32] mb-1">Phone</h3>
                    <a href="tel:+918882062043" className="text-slate-600 text-sm hover:text-[#0B1B32] transition-colors">
                      +91 8882062043
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-[#F8FAFC]">
                <h3 className="font-medium text-[#0B1B32] mb-2">Quick Response</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  We aim to respond to all inquiries within 24 hours during business days.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-medium text-[#0B1B32] mb-8">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-name"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      data-testid="contact-company"
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="contact-phone"
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Product Required</label>
                    <Input
                      type="text"
                      name="product"
                      placeholder="e.g., Basmati Rice"
                      value={formData.product}
                      onChange={handleChange}
                      data-testid="contact-product"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Quantity</label>
                    <Input
                      type="text"
                      name="quantity"
                      placeholder="e.g., 20 tons"
                      value={formData.quantity}
                      onChange={handleChange}
                      data-testid="contact-quantity"
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                  <Textarea
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    data-testid="contact-message"
                    className="w-full"
                    placeholder="Tell us about your requirements..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-contact-button"
                  className="w-full md:w-auto bg-[#0B1B32] text-white hover:bg-[#1e293b] rounded-none px-12 py-6 font-medium transition-all duration-300 text-sm tracking-wide shadow-lg hover:shadow-xl"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="pb-0">
        <div className="w-full h-[450px]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3505.0753456894655!2d77.27858231508047!3d28.5352599825742!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce3e564c5c3e9%3A0x1b8c5c5c5c5c5c5c!2sJasola%20Vihar%2C%20New%20Delhi%2C%20Delhi%20110076!5e0!3m2!1sen!2sin!4v1234567890"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Vista Global Trade Location"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default Contact;